import * as ko from 'knockout';

import { ListRequestParams } from '../api/request';
import { ListLoaderDelegate, delegateActions } from '../components/list_loader';
import { TraitCategory } from '../models/trait_category';
import * as traitCategoriesApi from '../api/trait_categories';
import * as driversApi from '../api/drivers';
import * as clientTypesApi from '../api/client_types';
import { session } from '../session';
import { ListHeaderAction } from '../components/list_header';
import i18n from '../i18n';
import { translate } from '../i18n_text';
import { canCreateTraitCategory, canDeleteTraitCategory, reactListTablesEnabled } from '../permissions';
import { BaseLoadingScreen } from './base_loading_screen';
import { GridView } from '../utils/grid_view';
import { GridChoices, listLookup } from '../utils/grid_choices';
import {
  TraitCategoriesListChoices,
  TraitCategoriesListPage,
  TraitCategoriesListProps,
  TraitCategoriesListQuery,
  TraitCategoriesListRow,
} from 'react-islands';

let template = require('raw-loader!../../templates/trait_categories.html').default;

class TraitCategoriesScreen
  extends BaseLoadingScreen
  implements ListLoaderDelegate<traitCategoriesApi.TraitCategoryData, TraitCategory>
{
  showDriver = session.tenant().tpp_enabled;
  canCreate = canCreateTraitCategory();
  canDelete = canDeleteTraitCategory();
  listActions = ko.observableArray<ListHeaderAction>([]);

  newTraitCategoriesTable = reactListTablesEnabled();

  private view = new GridView(
    'trait-categories',
    new GridChoices<TraitCategoriesListChoices>(
      {
        driver: listLookup(driversApi.list, 'driver'),
        client_type: listLookup(clientTypesApi.list, 'clientType'),
      },
      () => ({ driver: [], clientType: [] })
    )
  );

  tableProps = ko.pureComputed<TraitCategoriesListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
    showDriver: this.showDriver,
  }));

  constructor(params: { filters: unknown }) {
    super();

    if (this.canCreate) {
      this.listActions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: '/trait_categories/new/',
        tooltipTitle: i18n.t('Add Trait Category')(),
      });
    }

    if (this.newTraitCategoriesTable) {
      this.loadedAfter(this.view.load(params.filters));
      return;
    }

    this.loaded();
  }

  fetch(params: ListRequestParams) {
    return traitCategoriesApi.list(params);
  }

  instantiate(data: traitCategoriesApi.TraitCategoryData) {
    return new TraitCategory(data);
  }

  remove(id: string) {
    return traitCategoriesApi.remove(id);
  }

  canRemove(entity: TraitCategory) {
    return this.canDelete;
  }

  private fetchPage = (query: TraitCategoriesListQuery): Promise<TraitCategoriesListPage> =>
    // The endpoint applies the search box to the translated name.
    this.view.fetchListPage(query, traitCategoriesApi.list, 'name_prefix', this);

  toRow = (
    model: TraitCategory,
    traitCategory: traitCategoriesApi.TraitCategoryData
  ): TraitCategoriesListRow => {
    const driver = traitCategory.driver;

    return {
      id: traitCategory.id,
      name: translate(traitCategory.name_json),
      url: session.toTenantPath(`/trait_categories/${traitCategory.id}/`),
      driverName: driver ? translate(driver.name_json) : '',
      clientTypeName: driver ? translate(driver.client_type.name_json) : '',
      actions: delegateActions(this, model, this.view.reload),
    };
  };
}

export let traitCategories = {
  name: 'trait-categories',
  viewModel: TraitCategoriesScreen,
  template: template,
};

ko.components.register(traitCategories.name, traitCategories);
